import React, { Suspense } from 'react'
import PropTypes from 'prop-types'
import { Columns, Message, Section, Loader } from '@qure/containers'
import { Route, useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import useHomeData from 'libs/hooks/userData'
import useLicense from 'libs/hooks/useLicense'
import backendChecks from 'libs/backendChecks'
import useJiraIssueCollector from 'libs/useJiraIssueCollector'
import useSetMixpanelUser from 'libs/hooks/useSetMixpanelUser'
import CSVDownloadProgress from 'components/CSVDownloadProgress'

import Sidebar from '../Sidebar'
import Menubar from '../Menubar'
import UnAuthorized from '../UnAuthorized'
import AccessCheck from '../AccessCheck/index'
import LogoutConfirm from '../LogoutConfirm'
import CardLoader from '../Loaders/CardLoader'

import siteSchema from '../../schemas/siteSchema.json'
import '../../pages/Dashboard/index.scss'

const AppLayout = props => {
  const history = useHistory()
  const { pathname } = useLocation()

  const { children, setConfirmLogout, ...routeProps } = props
  const { isLoading: isProfileLoading, authData, hasError } = useHomeData()
  const { isLoading: isLicenseLoading } = useLicense(authData)
  const isLoading = isProfileLoading || isLicenseLoading

  const userData = _.get(authData, ['user'], null)
  useJiraIssueCollector(userData)
  useSetMixpanelUser(userData)

  const { t } = useTranslation()

  const sites = _.get(authData, ['sites'])

  const isSiteArrayEmpty = _.isEmpty(sites)
  const hasSiteJSONIssue =
    !isSiteArrayEmpty && !backendChecks.validateJSON(siteSchema, sites)
  const hasSiteIssue = isSiteArrayEmpty || hasSiteJSONIssue

  const isViewer = pathname.includes('viewer')

  const getPageContainer = () => {
    if (isLoading || hasSiteIssue) {
      return null
    }

    if (isViewer) {
      return (
        <AccessCheck>
          {({ isAuthorized, _isProfileLoading }) => {
            if (isAuthorized) {
              return (
                <Suspense
                  fallback={
                    <Loader
                      className="page-loader"
                      loadingText={{ title: t('Loading the page') }}
                    />
                  }
                >
                  {children}
                </Suspense>
              )
            }

            if (_isProfileLoading) {
              return <CardLoader t={t} />
            }

            return (
              <Section isFullHeight>
                <UnAuthorized />
              </Section>
            )
          }}
        </AccessCheck>
      )
    }

    return (
      <div className="page-top-container">
        <Columns isGapless isFullHeight>
          <Columns.Column isNarrow className="is-hidden-mobile">
            <Sidebar onLogout={setConfirmLogout} />
          </Columns.Column>
          <Columns.Column className="overflow-y has-full-viewport-height">
            <Menubar onLogout={setConfirmLogout} />
            {/* eslint-disable */}
            <AccessCheck>
              {({ isAuthorized, isProfileLoading }) =>
                isAuthorized ? (
                  <Suspense
                    fallback={
                      <Loader
                        className="page-loader"
                        loadingText={{ title: t('Loading the page') }}
                      />
                    }
                  >
                    {/* here goes the csv download progress */}
                    <CSVDownloadProgress />
                    <Section>{children}</Section>
                  </Suspense>
                ) : isProfileLoading ? (
                  <CardLoader t={t} />
                ) : (
                  <Section isFullHeight>
                    <UnAuthorized />
                  </Section>
                )
              }
            </AccessCheck>
            {/* eslint-enable */}
          </Columns.Column>
        </Columns>
      </div>
    )
  }

  if (hasError) {
    history.push(`/login?redirect=true${isViewer ? `&next=${pathname}` : ''}`)
    return null
  }

  return (
    <Route {...routeProps}>
      {isLoading && <CardLoader t={t} />}

      {!isLoading && isSiteArrayEmpty ? (
        <div className="mt-6">
          <Message
            title={t('Site Mapping not found')}
            subtitle={t('Please contact administrator')}
            iconClass="fa-info-circle"
            color="primary"
            subtitleColor="light-grey"
          />
        </div>
      ) : (
        hasSiteJSONIssue && (
          <div className="mt-6">
            <Message
              title={t('Site Mapping not proper')}
              subtitle={t(
                'Please add id,name and additional field in site mapping'
              )}
              iconClass="fa-info-circle"
              color="primary"
              subtitleColor="light-grey"
            />
          </div>
        )
      )}

      {getPageContainer()}
    </Route>
  )
}

AppLayout.propTypes = {
  children: PropTypes.node.isRequired,
  setConfirmLogout: PropTypes.func.isRequired
}

export default LogoutConfirm(AppLayout)
